import React, { useCallback } from 'react';
import { Box, Text, VStack, HStack, Icon, useToast } from '@chakra-ui/react';
import { RiUploadLine, RiFileTextLine, RiDeleteBinLine } from 'react-icons/ri';

import {
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_SIZE_BYTES,
} from '../../../utils/qualityControlConstants';

interface FilePickerProps {
  id: string;
  files: File[];
  onChange: (files: File[]) => void;
  isDisabled?: boolean;
  error?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Seletor de múltiplos arquivos (imagem/PDF, máx. 5MB cada), controlado
 * (`files`/`onChange`), sem dependência de Unform — usado tanto pelo
 * Global/UploadMultiple (dentro de formulário) quanto diretamente na tela de
 * detalhe da NC (fora de um <Form>, para anexar evidências depois de criada).
 */
export const FilePicker: React.FC<FilePickerProps> = ({
  id,
  files,
  onChange,
  isDisabled,
  error,
}) => {
  const toast = useToast();

  const handleFilesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files || []);
      e.target.value = '';

      const accepted: File[] = [];
      selected.forEach(file => {
        if (!EVIDENCE_ALLOWED_TYPES.includes(file.type)) {
          toast({
            title: 'Tipo de arquivo não permitido',
            description: `${file.name}: apenas imagens (JPG, PNG) e PDF são aceitos`,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        if (file.size > EVIDENCE_MAX_SIZE_BYTES) {
          toast({
            title: 'Arquivo muito grande',
            description: `${file.name}: o arquivo deve ter no máximo 5MB`,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        accepted.push(file);
      });

      if (accepted.length > 0) {
        onChange([...files, ...accepted]);
      }
    },
    [files, onChange, toast],
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      onChange(files.filter((_, i) => i !== index));
    },
    [files, onChange],
  );

  return (
    <Box width="100%" mb={2}>
      {!isDisabled && (
        <Box
          as="label"
          htmlFor={id}
          borderWidth={2}
          borderStyle="dashed"
          borderColor={error ? 'red.500' : 'gray.600'}
          borderRadius={8}
          p={6}
          cursor="pointer"
          _hover={{ borderColor: 'blue.500', bg: 'gray.700' }}
          transition="all 0.2s"
          display="block"
          width="100%"
        >
          <VStack spacing={2}>
            <Icon as={RiUploadLine} fontSize="3xl" color="gray.400" />
            <Text color="gray.300" fontSize="sm" textAlign="center">
              Clique ou arraste as evidências aqui (pode selecionar várias)
            </Text>
            <Text color="gray.500" fontSize="xs" textAlign="center">
              JPG, PNG ou PDF até 5MB cada
            </Text>
          </VStack>
          <input
            id={id}
            type="file"
            multiple
            accept={EVIDENCE_ALLOWED_TYPES.join(',')}
            onChange={handleFilesChange}
            style={{ display: 'none' }}
          />
        </Box>
      )}

      {files.length > 0 && (
        <VStack spacing={2} mt={!isDisabled ? 3 : 0} align="stretch">
          {files.map((file, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${file.name}-${file.size}-${index}`}
              borderWidth={1}
              borderColor="green.500"
              borderRadius={8}
              p={4}
              bg="gray.700"
            >
              <HStack justify="space-between">
                <HStack spacing={3} minW="0">
                  <Icon as={RiFileTextLine} fontSize="2xl" color="green.500" />
                  <VStack align="start" spacing={0} minW="0">
                    <Text fontSize="sm" color="gray.100" wordBreak="break-all">
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      {formatFileSize(file.size)}
                    </Text>
                  </VStack>
                </HStack>
                {!isDisabled && (
                  <Box
                    as="button"
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    p={2}
                    borderRadius={4}
                    _hover={{ bg: 'red.700' }}
                    transition="all 0.2s"
                  >
                    <Icon as={RiDeleteBinLine} fontSize="lg" color="red.400" />
                  </Box>
                )}
              </HStack>
            </Box>
          ))}
        </VStack>
      )}

      {error && (
        <Text color="red.500" fontSize="xs" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
};
